/**
 * Round 6 (WP3): the words of the runs list, the run detail and the distributions list's run
 * links (prototype 30-run). Sentence case, plain words; names, counts and times come in as
 * arguments, never as sample values.
 */
import { pluralize } from "../services/distributions/distributionFormat";
import { DISTRIBUTION_ARCHIVE_COPY } from "./distributionHelpContent";

const quoted = (name: string): string => `“${name}”`;

export const DISTRIBUTION_RUN_TRIGGER_LABEL = {
  schedule: "Scheduled",
  now: "Send now",
  test: "Test",
  preview: "Preview",
} as const;

export const DISTRIBUTION_RUN_STATE_COPY = {
  queued: "Waiting to start",
  running: "Sending now",
  failed: "Failed",
  preview: "Preview only",
  attention: "Needs attention",
  warnings: "Sent with warnings",
  delivered: "Delivered",
  testSent: "Test sent",
  nothingSent: "Nothing sent",
  didntArrive: (n: number) => `${n} didn’t arrive`,
  held: (n: number) => `${n} held`,
  notes: (n: number) => pluralize(n, "note", "notes"),
  skippedNoData: (n: number) => `${n} skipped, no data`,
  sentOf: (sent: number, total: number) => `${sent.toLocaleString()} of ${total.toLocaleString()} sent`,
  sentTo: (who: string) => `Sent to ${who}`,
  schedule: "Schedule",
  someone: "Someone",
} as const;

export const DISTRIBUTION_RUN_LIST_COPY = {
  crumb: "Runs",
  title: "Send history",
  count: (runs: number, attention: number) =>
    `${pluralize(runs, "run", "runs")}${attention ? ` · ${attention} ${attention === 1 ? "needs" : "need"} attention` : ""}`,
  segmentsLabel: "Show runs",
  segments: { all: "All", attn: "Needs attention", ok: "Delivered", test: "Tests" },
  triggerLabel: "Trigger",
  allTriggers: "All triggers",
  caption: (name: string) => `Runs of ${name}, newest first`,
  columns: { run: "Run", trigger: "Trigger", status: "Status", made: "Copies made", sent: "Sent", failed: "Failed", skipped: "Skipped", startedBy: "Started by", open: "Open" },
  view: "View",
  openLabel: (label: string) => `View the run from ${label}`,
  notSent: "Not sent",
  noMatch: "No runs match these filters.",
  showAll: "Show all runs",
  showing: (shown: number, total: number) => `Showing ${shown} of ${total}`,
  clearFilters: "Clear filters",
  footNote: "Tests and previews don’t send anything to recipients. Files from each run are kept for a limited time.",
  filesKept: "Files are kept for a limited time.",
  loadOlder: "Load older runs",
  /** F19 table: the columns and the Result words. */
  f19Columns: { run: "When", status: "Result", made: "Copies", sent: "Sent", failed: "Didn’t arrive", skipped: "Skipped", startedBy: "Started by" },
  result: { sending: "Sending", waiting: "Waiting to start", failed: "Failed", problems: "Finished with problems", delivered: "Delivered", test: "Test", preview: "Preview" },
  loading: "Loading the runs…",
  loadError: "The runs couldn’t be loaded",
  retry: "Try again",
  /** The server's own message is shown under the title; this tags it. */
  fromServer: (message: string) => `${message} (from the server)`,
  archived: {
    title: "This distribution was archived",
    body: "It no longer sends and has left the list. Restore it to bring it back.",
    back: "Back to the list",
    restore: "Restore",
    restoring: "Restoring…",
    restoreFailed: "Couldn’t restore it",
  },
  empty: {
    title: "No runs yet",
    notActivated: { title: "Still a draft", caption: "Nothing has been sent.", action: "Continue setup" },
    scheduled: { title: (date: string) => `First send ${date}`, caption: "Runs appear here after it goes out.", action: "Send a test" },
    notScheduled: { title: "Nothing scheduled", caption: "It only sends when you press Send.", action: "Send to everyone now" },
    paused: { title: "Paused", caption: "Nothing has been sent yet." },
    none: "Send it now, or edit it to set a schedule.",
  },
  gone: { title: "This distribution isn’t here anymore", body: "It may have been deleted.", action: "Back to the list" },
} as const;

export const DISTRIBUTION_HEADER_COPY = {
  subline: (field: string, recipients: number, copies: number | null) =>
    [`Split by ${field}`, copies !== null ? pluralize(copies, "copy", "copies") : null, pluralize(recipients, "recipient", "recipients")].filter(Boolean).join(" · "),
  nextRun: (when: string) => `Next run ${when}`,
  paused: "Paused. No runs until you resume.",
  finished: "Finished. No more runs.",
  notScheduled: "Not scheduled yet",
  sendNow: "Send now",
  sending: "Sending…",
  pause: "Pause",
  resume: "Resume",
  edit: "Edit",
  resumeEditing: "Resume editing",
  more: (name: string) => `More actions for ${name}`,
  menu: {
    preview: "Preview what people get",
    copyLink: "Copy link",
    delete: "Delete",
  },
  confirm: {
    lead: (name: string) => `Send ${quoted(name)} now?`,
    message: (recipients: number, paused: boolean) =>
      `${pluralize(recipients, "recipient gets", "recipients get")} a copy now, with the latest data. ${paused ? "The schedule stays paused." : "The schedule doesn’t change."}`,
    cancel: "Cancel",
    send: "Send now",
    sending: "Sending…",
  },
  linkCopied: { title: "Link copied", body: "Editors of this story can open it." },
  linkFailed: "Copy is blocked here. Copy the address from the browser bar instead.",
  pauseFailed: "Couldn’t pause it",
  resumeFailed: "Couldn’t resume it",
} as const;

export const DISTRIBUTION_LIVE_COPY = {
  title: (trigger: string) => `${trigger} in progress`,
  caption: (by: string, at: string) => `Started by ${by} at ${at}. You can leave this page. You’ll get a notification when it’s done.`,
  captionNoTime: (by: string) => `Started by ${by}. You can leave this page. You’ll get a notification when it’s done.`,
  steps: {
    check: "Check the data",
    make: "Make copies",
    files: "Check files",
    send: "Send",
  },
  stepDetail: {
    checking: "Making sure the data is fresh",
    fresh: (asOf: string) => `Fresh as of ${asOf}`,
    made: (made: number, copies: number) => `${made} of ${copies} made`,
    filesTodo: "Every page checked before sending",
    filesDone: "Checked",
    sent: (sent: number, total: number) => `${sent} of ${total} sent`,
  },
  stepsLabel: "Run progress",
  stepStates: { done: "done", current: "in progress", todo: "not started", error: "failed" },
  now: {
    check: "Checking the data",
    make: (n: number, of: number) => `Making copy ${Math.min(n + 1, of)} of ${of}`,
    send: (n: number, of: number) => (of ? `Sending ${Math.min(n + 1, of)} of ${of}` : "Sending"),
  },
  counts: (sent: number, failed: number, skipped: number) => `${sent} sent · ${failed} failed · ${skipped} skipped`,
  progressLabel: "Run progress",
  viewRun: "View run",
} as const;

export const DISTRIBUTION_RUN_DETAIL_COPY = {
  crumb: (date: string) => `${date} run`,
  back: (name: string) => `All runs of ${name}`,
  pager: { label: "Other runs", newer: "Newer", older: "Older" },
  kicker: { before: "Run of ", split: (field: string) => ` · Split by ${field}` },
  started: {
    schedule: (at: string) => `Started on schedule at ${at}`,
    now: (by: string, at: string) => `Send now by ${by} at ${at}`,
    test: (by: string, at: string) => `Test by ${by} at ${at}`,
    preview: (by: string, at: string) => `Preview by ${by} at ${at}`,
    waiting: "Waiting to start",
  },
  asOf: (when: string) => `Data as of ${when}`,
  took: (duration: string) => `Took ${duration}`,
  stoppedAfter: (duration: string) => `Stopped after ${duration}`,
  seeWhatPeopleGot: "See what people got",
  more: "More actions for this run",
  menu: { copyLink: "Copy link to this run", sendNow: "Send now", edit: "Edit distribution" },
  notes: {
    test: { lead: "Test run.", text: (to: string, as: string) => `One copy went to ${to}, made as ${as}’s copy. Recipients didn’t get anything.` },
    preview: { lead: "Preview only.", text: "These copies were made so you could check them. Nothing was sent." },
  },
  failed: { title: "This run failed. Nothing was sent.", howLabel: "How to fix:", sendNow: "Send now" },
  stats: {
    made: "Copies made",
    madeSub: (field: string) => `One for each ${field}`,
    madeShort: (n: number) => `${n} had no data`,
    recipients: "People",
    sent: "Sent",
    previewed: "Previewed",
    sentSub: { warned: (n: number) => (n === 1 ? "1 with a note" : `${n} with notes`), some: "Got their copy", none: "None yet", nothing: "Nothing sent" },
    failed: "Didn’t arrive",
    failedSub: { some: "Didn’t arrive. See below.", none: "None" },
    skipped: "Skipped",
    skippedSub: { fix: (n: number) => `${n} ${n === 1 ? "needs" : "need"} a fix`, noData: "No data, nothing to fix", none: "None" },
  },
  issues: {
    groups: { failed: "Didn’t arrive", held: "Held, not sent", warned: "Sent with a note", warnedPreview: "Made with a note" },
    description: {
      failed: (sent: number) => `${sent.toLocaleString()} got their copy. Fix what’s below, then send again.`,
      held: (n: number) => `${n === 1 ? "One person" : `${n} people`} didn’t get a copy. Each one says why and how to fix it.`,
      notes: "Everyone got their copy. Each note says what was off and whether you need to do anything.",
    },
    resend: (n: number) => `Resend to these ${n}`,
    resendOne: "Resend",
    downloadAll: "Download all files",
    downloadingAll: "Preparing files…",
    downloadAllFailed: "Couldn’t make the zip. Try again.",
    comingSoon: "Coming soon",
    status: { failed: "Failed", held: "Held", warned: "Sent with a note", previewWarned: "Preview has a note" },
    meta: (value: string) => `${value} copy`,
    fixRow: "Open their row in Recipients",
    copyInvite: "Copy /invite @MagicBI",
    inviteCopied: (channel: string) => ({ title: "Copied /invite @MagicBI", body: `Paste it in ${channel}, then send again.` }),
    seeWhy: "See why",
    wouldHaveGot: "See what they would have got",
    got: "See what they got",
  },
  howLabel: "How to fix:",
  logTitle: "What happened, step by step",
  recipients: "Recipients",
  inThisRun: (n: number) => `${n.toLocaleString()} in this run`,
  loading: "Loading this run…",
  loadError: "This run couldn’t be loaded",
  fromServer: (message: string) => `${message} (from the server)`,
  retry: "Try again",
  gone: { title: "This run isn’t here anymore", body: "It may have been removed.", action: "Back to runs" },
} as const;

export const DISTRIBUTION_LIST_RUNS_COPY = {
  column: "Last run",
  viewRuns: "View runs",
  viewRunsAria: (name: string) => `View runs of ${name}`,
  openRun: (label: string) => `. Open the run from ${label}`,
  noRuns: "No runs yet",
  firstRun: (when: string) => `First run ${when}`,
  started: (time: string, trigger: string) => `Started ${time} · ${trigger}`,
  when: (date: string, trigger: string) => `${date} · ${trigger}`,
  menu: {
    viewRuns: "View runs",
    viewRunsHint: (sends: number) => (sends ? `${pluralize(sends, "send", "sends")} so far, with every recipient` : "Nothing sent yet"),
    sendNow: "Send now",
    sendNowHint: "Everyone gets a copy now",
  },
  footNote: "Last run is the latest scheduled or Send now run. Open it to see every recipient, or View runs for the full history, tests and previews.",
} as const;

/** The story Share menu's live line under "Personalized distribution". */
export const DISTRIBUTION_SHARE_NOTE_COPY = {
  setUp: (n: number) => `${n} set up for this story`,
  sending: (n: number) => `${n} sending now`,
  attention: (n: number) => `${n} ${n === 1 ? "needs" : "need"} attention`,
} as const;

/** What one recipient got (prototype `recvSkipped`, `recvTest`). */
export const DISTRIBUTION_RECEIVED_COPY = {
  skipTitle: (name: string) => `Nothing was sent to ${name} in this run`,
  fixRow: "Open their row in Recipients",
  howLabel: "How to fix:",
  test: {
    title: "Test copy.",
    text: (name: string, value: string, to: string) => `This is ${name}’s ${value} copy, sent only to ${to}. Recipients didn’t get anything.`,
  },
} as const;


/** The runs page (F19): header, ⋯ menu, Send to everyone dialog, archive dialog and the "What people get" card. */
export const DISTRIBUTION_RUNS_PAGE_COPY = {
  actions: { sendNow: "Send to everyone now", pause: "Pause sending", resume: "Resume sending", duplicate: "Duplicate" },
  menu: { duplicate: "Duplicate", copyLink: "Copy link", archive: "Archive" },
  summary: {
    one: "Sends one copy per ",
    people: (n: number) => pluralize(n, "person", "people"),
    to: " to ",
    noOne: "no one yet",
    notScheduled: "not scheduled yet",
    next: (when: string) => ` Next send ${when}.`,
    paused: (since: string | null) => ` ${since ?? "Paused"}.`,
    pausedNext: (when: string, since: string | null) => ` ${since ?? "Paused"}. Next send would be ${when}.`,
    finished: (when: string | null) => (when ? ` Its one send went out ${when}.` : " It has finished."),
    ariaLabel: "What this distribution does",
  },
  send: {
    title: (name: string) => `Send ${quoted(name)} to everyone now?`,
    description: (people: string, paused: boolean) => `${people} get a copy now, with the latest data. ${paused ? "The schedule stays paused." : "The schedule doesn’t change."}`,
    rows: { to: "To", files: "Files", after: "After this" },
    afterActive: "The schedule continues",
    afterPaused: "The schedule stays paused",
    confirm: "Send now",
    sending: "Sending…",
    cancel: "Cancel",
    close: "Close",
  },
  archive: {
    title: (name: string) => `Archive ${quoted(name)}?`,
    // One wording for both archive dialogs (list and runs page).
    message: DISTRIBUTION_ARCHIVE_COPY.message,
    confirm: DISTRIBUTION_ARCHIVE_COPY.confirm,
    cancel: DISTRIBUTION_ARCHIVE_COPY.cancel,
    close: DISTRIBUTION_ARCHIVE_COPY.close,
    failed: "Couldn’t archive it",
  },
  side: {
    title: "What people get",
    splitBy: "Split by",
    people: "People",
    files: "Files",
    look: "Look",
    audience: "Audience",
    audienceInside: "People inside your organisation",
    audienceOutside: "People outside your organisation",
    copies: (field: string, n: number) => `${field} · ${n ? pluralize(n, "copy", "copies") : "no copies yet"}`,
    thumbsLabel: "Live copies",
    thumbLabel: (who: string) => `${who}’s copy`,
    peopleValue: (n: number, source: string | null) => `${n.toLocaleString()}${source ? ` · ${source}` : ""}`,
    lookValue: (theme: string | null, kit: string | null) => [theme, kit].filter(Boolean).join(" · "),
    sourceList: "from a list",
    sourceModel: "from the data model",
    formats: { pdf: "PDF", pptx: "PowerPoint" },
    messageOnly: "A message only",
    previewLink: "Preview as anyone",
    previewTitle: "Preview as anyone",
    previewClose: "Close",
    previewArchived: "An archived distribution can’t be previewed. Restore it first.",
    previewNoCopies: "Nobody is ready to receive a copy yet, so there is nothing to preview.",
    picker: { label: "Preview as", placeholder: "Choose one", empty: "Nothing matches", search: "Search", menu: "Preview as", previous: "Previous", next: "Next" },
    position: (index: number, total: number) => `${index} of ${total}`,
  },
} as const;

/** "Paused 3 days ago by Priya": who paused it and when. `who` is omitted when the API doesn't say. */
export const DISTRIBUTION_PAUSED_COPY = {
  since: (when: string, who: string | null): string => `Paused ${when}${who ? ` by ${who}` : ""}`,
} as const;

/** A run notification's time lines; `when` is the relative time ("2 minutes ago"). */
export const DISTRIBUTION_RUN_TIME_COPY = {
  queued: (when: string): string => `Queued ${when}`,
  started: (when: string): string => `Started ${when}`,
  finished: (when: string): string => `Finished ${when}`,
} as const;
